import * as ContextMenuPrimitive from 'radix-ui/context-menu';
import { type ReactNode, useId } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { MENU_SURFACE, MENU_VIEWPORT } from '../SelectMenu/SelectMenu';

/**
 * THE MENU THAT OPENS AT THE THING IT ACTS ON (spec/anatomy/MACOS.md "Menus and context
 * menus"; spec/screens/list.md section 2 item 8).
 *
 * `DropdownMenu` next door is anchored to a BUTTON: something on screen was pressed and
 * the panel hangs off it. A context menu has no button - it opens where the secondary
 * click landed, or under the finger that held still - so it cannot be that component with
 * another prop. Radix ships the two as separate primitives for the same reason, and the
 * touch half is the part that would otherwise have to be written by hand: `ContextMenu`
 * opens on a long press on a coarse pointer and on `contextmenu` on a fine one, which is
 * exactly the pair the HIG asks for (macOS secondary click, iOS touch and hold).
 *
 * IT IS THE SAME MENU, drawn from the same two constants `DropdownMenu` uses
 * (`MENU_SURFACE`, `MENU_VIEWPORT`) and the same item class: one menu language in the
 * product, whatever opened it. The kit says the same thing - a near-opaque surface with a
 * 20px blur and 12px corners is `Menus/Light/Examples/Regular` whether the menu hangs off
 * a toolbar item or off a row.
 *
 * WHAT IT DOES NOT HAVE, on purpose: no `align` (the pointer decides), no checkbox or
 * radio items (a context menu holds ACTIONS; a state a person toggles belongs in the view
 * options), and no `className` - the rule `ui-foundation.md` D1 states for every floating
 * primitive.
 */
export interface ContextMenuProps {
  /** What the menu opens ON. Rendered as-is; the primitive attaches the gesture to it. */
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  /**
   * Fires when the menu opens and when it closes. The list uses it to keep the row that
   * was right-clicked selected while the menu stands open, which is what makes the menu
   * act on what the reader is looking at rather than on an older selection.
   */
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  /** A menu with nothing in it must not open at all; the caller says when that is. */
  readonly disabled?: boolean | undefined;
}

/**
 * Shared with the submenu trigger, so a row that opens a submenu is the same row as one
 * that runs an action. `data-highlighted` is Radix's one highlight (keyboard and pointer)
 * and `.pressable` is the touch half - the pair `DropdownMenu` documents.
 */
/**
 * NO INK COLOUR IN HERE (P4 round 2, finding F2). It carried `text-ink`, and a destructive
 * item then set `text-danger` BESIDE it on the same element - two utilities for one property,
 * decided by their order in the generated stylesheet rather than by the order they are
 * written in, and `text-ink` won: the menu shipped a black "Archive" with a red trash symbol,
 * which is neither of the two things Apple draws. The colour is the item's own decision now.
 */
const ITEM =
  'pressable flex min-h-(--touch-target-min) items-center gap-2.5 rounded-control px-2.5 text-sm ' +
  'outline-none select-none data-highlighted:bg-surface-hover ' +
  'data-disabled:opacity-50';

const CONTENT = cx(MENU_SURFACE, MENU_VIEWPORT, 'min-w-52 p-1');

export function ContextMenu({ trigger, children, onOpenChange, disabled }: ContextMenuProps) {
  return (
    <ContextMenuPrimitive.Root {...(onOpenChange === undefined ? {} : { onOpenChange })}>
      <ContextMenuPrimitive.Trigger asChild {...(disabled === undefined ? {} : { disabled })}>
        {trigger}
      </ContextMenuPrimitive.Trigger>
      <ContextMenuPrimitive.Portal>
        {/* `collisionPadding` rather than a placement: the pointer chose the position and
            the only thing left to decide is what happens at the edge of the window. */}
        <ContextMenuPrimitive.Content collisionPadding={8} className={CONTENT}>
          {children}
        </ContextMenuPrimitive.Content>
      </ContextMenuPrimitive.Portal>
    </ContextMenuPrimitive.Root>
  );
}

export interface ContextMenuItemProps {
  readonly onSelect: () => void;
  readonly children: ReactNode;
  readonly icon?: IconName | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * `danger` is the destructive item and it is drawn in red - the LABEL as well as the
   * symbol (round 2, F2; kit Menus/Light/Regular, where the destructive row is red type). The
   * HIG puts it LAST and behind a separator; that ordering belongs to the caller, because
   * only the caller knows what else is in the menu.
   */
  readonly tone?: 'default' | 'danger';
}

export function ContextMenuItem({
  onSelect,
  children,
  icon,
  disabled = false,
  tone = 'default',
}: ContextMenuItemProps) {
  return (
    <ContextMenuPrimitive.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cx(ITEM, tone === 'danger' ? 'text-danger' : 'text-ink')}
    >
      {icon !== undefined && (
        <Icon name={icon} className={tone === 'danger' ? 'text-danger' : 'text-ink-subtle'} />
      )}
      {children}
    </ContextMenuPrimitive.Item>
  );
}

export function ContextMenuSeparator() {
  return <ContextMenuPrimitive.Separator className="my-1 h-px bg-line" />;
}

export function ContextMenuLabel({ children }: { readonly children: ReactNode }) {
  return (
    <ContextMenuPrimitive.Label className="px-2.5 py-2 text-2xs font-semibold tracking-widest text-ink-subtle uppercase">
      {children}
    </ContextMenuPrimitive.Label>
  );
}

export interface ContextMenuSubProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly icon?: IconName | undefined;
  readonly disabled?: boolean | undefined;
}

/**
 * A SUBMENU IS A CHEVRON AND A SECOND PANEL, never a nested list drawn inline: the kit's
 * submenu row is the ordinary item with a chevron in its trailing slot
 * (`Menus/Light/Regular/Menu without Selection/Items - Submenu/1 - Idle`), and the panel it
 * opens is the same surface as its parent.
 */
export function ContextMenuSub({ label, children, icon, disabled = false }: ContextMenuSubProps) {
  const id = useId();
  return (
    <ContextMenuPrimitive.Sub>
      <ContextMenuPrimitive.SubTrigger id={id} disabled={disabled} className={cx(ITEM, 'text-ink')}>
        {icon !== undefined && <Icon name={icon} className="text-ink-subtle" />}
        <span className="flex-1">{label}</span>
        <Icon name="chevron-right" size="0.875rem" className="text-ink-subtle" />
      </ContextMenuPrimitive.SubTrigger>
      <ContextMenuPrimitive.Portal>
        <ContextMenuPrimitive.SubContent
          collisionPadding={8}
          sideOffset={2}
          className={CONTENT}
          aria-labelledby={id}
        >
          {children}
        </ContextMenuPrimitive.SubContent>
      </ContextMenuPrimitive.Portal>
    </ContextMenuPrimitive.Sub>
  );
}
